import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Upload, Trash2, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { resolveImageUrl } from "@/lib/images";

const MAX_INPUT_MB = 10;
const TARGET = 600;

async function shrink(file) {
  const img = document.createElement("img");
  const url = URL.createObjectURL(file);
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = url;
  });
  const scale = Math.min(TARGET / img.width, TARGET / img.height, 1);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(url);
  const keepAlpha = /png|webp/i.test(file.type);
  const source = keepAlpha ? trimTransparent(canvas, ctx) : canvas;
  const blob = await new Promise((resolve) =>
    source.toBlob(resolve, keepAlpha ? "image/png" : "image/jpeg", 0.88)
  );
  return { blob, name: keepAlpha ? "prize.png" : "prize.jpg" };
}

// Crop away fully transparent margins so the picture fills the reel window.
function trimTransparent(canvas, ctx) {
  const { width: w, height: h } = canvas;
  const { data } = ctx.getImageData(0, 0, w, h);
  let top = h, left = w, right = -1, bottom = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 8) {
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
  }
  if (right < 0 || (left === 0 && top === 0 && right === w - 1 && bottom === h - 1)) return canvas;
  const pad = 4;
  const sx = Math.max(0, left - pad);
  const sy = Math.max(0, top - pad);
  const sw = Math.min(w, right + pad + 1) - sx;
  const sh = Math.min(h, bottom + pad + 1) - sy;
  const out = document.createElement("canvas");
  out.width = sw;
  out.height = sh;
  out.getContext("2d").drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
  return out;
}

export default function SegmentImageUploader({ value, onChange, testId }) {
  const { t } = useTranslation();
  const inputRef = useRef();
  const [loading, setLoading] = useState(false);

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_INPUT_MB * 1024 * 1024) {
      toast.error(t("bg.too_large", { mb: MAX_INPUT_MB }));
      return;
    }
    if (!/^image\/(jpeg|png|webp|jpg)/i.test(file.type)) {
      toast.error(t("bg.wrong_format"));
      return;
    }
    setLoading(true);
    try {
      const { blob, name } = await shrink(file);
      const fd = new FormData();
      fd.append("file", blob, name);
      const { data } = await api.post("/uploads/game-background", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success(t("bg.uploaded"));
    } catch (err) {
      toast.error(formatApiError(err) || t("bg.process_error"));
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const preview = resolveImageUrl(value);

  return (
    <div
      className="mt-2 rounded-lg border border-border/70 bg-muted/30 p-2"
      data-testid={testId}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="relative h-11 w-11 shrink-0 overflow-hidden rounded-md border border-border"
          style={{
            backgroundColor: "#e5e7eb",
            backgroundImage:
              "linear-gradient(45deg, #cbd5e1 25%, transparent 25%, transparent 75%, #cbd5e1 75%), linear-gradient(45deg, #cbd5e1 25%, transparent 25%, transparent 75%, #cbd5e1 75%)",
            backgroundSize: "10px 10px",
            backgroundPosition: "0 0, 5px 5px",
          }}
          data-testid={`${testId}-preview`}
        >
          {preview ? (
            <img src={preview} alt="" className="h-full w-full object-contain" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-slate-500">
              <ImageIcon className="h-4 w-4" />
            </div>
          )}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[11px] font-semibold leading-tight">
            {t("game_editor.segment_image")}
          </div>
          <div className="mt-1.5 flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              type="button"
              className="h-7 rounded-md px-2 text-[11px]"
              onClick={() => inputRef.current?.click()}
              disabled={loading}
              data-testid={`${testId}-upload`}
            >
              <Upload className="mr-1 h-3 w-3" />
              {value
                ? t("game_editor.segment_image_replace")
                : t("game_editor.segment_image_add")}
            </Button>
            {value && (
              <button
                type="button"
                onClick={() => onChange(null)}
                disabled={loading}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                data-testid={`${testId}-remove`}
                aria-label={t("common.delete")}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="mt-1.5 text-[10px] leading-snug text-muted-foreground">
        {t("game_editor.segment_image_hint")}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onFile}
        className="hidden"
        data-testid={`${testId}-input`}
      />
    </div>
  );
}
